import React from 'react';

import {
	Button,
	ButtonGroup,
	CloseButton,
	Dialog,
	Drawer,
	Flex,
	Portal,
	Show,
} from '@welbe/design-system';

import {
	LuArrowRight,
	LuMerge,
	LuPen,
	LuUserCheck,
	LuUserX2,
} from 'react-icons/lu';

type EmployeeButtonsProps = {
	open: boolean;
	sires?: boolean;
	selectedEmployees: any[];
	isLoadingActiveCandidate: boolean;
	hasOnlyActive: boolean;
	hasOnlyInactive: boolean;
	onClose: () => void;
	handleEditEmployee: () => void;
	handleActiveEmployee: () => void;
	handleShowModalInactiveModal: () => void;
	handleOpenModalAssignJobProfile: () => void;
	handleMergeEmployee: () => void;
};

export const EmployeeButtons = ({
	open,
	selectedEmployees,
	isLoadingActiveCandidate,
	hasOnlyActive,
	hasOnlyInactive,
	sires,
	handleEditEmployee,
	handleMergeEmployee,
	handleActiveEmployee,
	handleShowModalInactiveModal,
	handleOpenModalAssignJobProfile,
}: EmployeeButtonsProps): JSX.Element => {
	const disableDeactivateButton = !hasOnlyActive || isLoadingActiveCandidate;
	const disableReactivateButton = !hasOnlyInactive || isLoadingActiveCandidate;

	return (
		<Drawer.Root
			placement="bottom"
			open={open}
			size="lg"
			closeOnInteractOutside={false}
			modal={false}
		>
			<Portal>
				<Drawer.Positioner pointerEvents="none">
					<Drawer.Content>
						<Drawer.Body p="1rem">
							<Flex gap="1rem" justifyContent="space-between" pb="1rem">
								<ButtonGroup size="sm">
									<Button
										size="sm"
										variant={disableReactivateButton ? 'solid' : 'surface'}
										id="reactivateEmployee"
										loading={isLoadingActiveCandidate}
										disabled={disableReactivateButton}
										onClick={handleActiveEmployee}
									>
										Reactivar
										<LuUserCheck />
									</Button>
									<Button
										size="sm"
										colorPalette={disableDeactivateButton ? 'gray' : 'red'}
										variant={disableDeactivateButton ? 'solid' : 'surface'}
										id="deactivateEmployee"
										onClick={handleShowModalInactiveModal}
										disabled={disableDeactivateButton}
										loading={isLoadingActiveCandidate}
									>
										Dar de baja
										<LuUserX2 />
									</Button>
								</ButtonGroup>
								<ButtonGroup size="sm" position="relative">
									<FusionButton
										sires={sires}
										onOpenMerge={handleMergeEmployee}
										activeProfiles={hasOnlyActive}
										insufficientEmployees={selectedEmployees?.length < 2}
										excessEmployees={selectedEmployees?.length > 2}
										loading={isLoadingActiveCandidate}
									/>
									<Button
										id="linkRiskProfile"
										onClick={handleOpenModalAssignJobProfile}
										loading={isLoadingActiveCandidate}
										width={'200px'}
									>
										Asignar perfil
										<LuArrowRight />
									</Button>

									<Button
										id="editEmployee"
										onClick={handleEditEmployee}
										disabled={selectedEmployees.length !== 1}
										loading={isLoadingActiveCandidate}
									>
										Editar colaborador
										<LuPen />
									</Button>
								</ButtonGroup>
							</Flex>
						</Drawer.Body>
					</Drawer.Content>
				</Drawer.Positioner>
			</Portal>
		</Drawer.Root>
	);
};
const Trigger = () => {
	return (
		<Dialog.Trigger asChild>
			<Button id="mergeEmployee">
				Fusionar expediente
				<LuMerge />
			</Button>
		</Dialog.Trigger>
	);
};

type FusionButtonProps = {
	onOpenMerge: () => void;
	sires?: boolean;
	activeProfiles: boolean;
	insufficientEmployees: boolean;
	excessEmployees: boolean;
	loading?: boolean;
};

const FusionButton = ({
	onOpenMerge,
	sires,
	activeProfiles,
	insufficientEmployees,
	excessEmployees,
	loading,
}: FusionButtonProps) => {
	const [open, setOpen] = React.useState(false);
	const message = React.useMemo(() => {
		if (insufficientEmployees) {
			return 'Seleccione al menos 2 expedientes.';
		}
		if (excessEmployees) {
			return 'Seleccione un máximo de 2 expedientes.';
		}

		if (!activeProfiles) {
			return 'Solo expedientes con perfil activo pueden ser fusionados';
		}

		return '';
	}, [insufficientEmployees, excessEmployees, activeProfiles]);

	if (!sires) {
		return null;
	}

	return (
		<Dialog.Root
			role="alertdialog"
			open={open}
			onOpenChange={({ open }: { open: boolean }) => setOpen(open)}
		>
			<Show
				when={activeProfiles && !insufficientEmployees && !excessEmployees}
				fallback={<Trigger />}
			>
				<Button id="mergeEmployee" onClick={onOpenMerge} loading={loading}>
					Fusionar expediente
					<LuMerge />
				</Button>
			</Show>

			<Portal>
				<Dialog.Backdrop />
				<Dialog.Positioner>
					<Dialog.Content>
						<Dialog.Header>
							<Dialog.Title>¡Atención!</Dialog.Title>
						</Dialog.Header>
						<Dialog.Body>
							<p>{message}</p>
						</Dialog.Body>
						<Dialog.Footer>
							<Dialog.ActionTrigger asChild>
								<Button variant="outline">Entendido</Button>
							</Dialog.ActionTrigger>
						</Dialog.Footer>
						<Dialog.CloseTrigger asChild>
							<CloseButton size="sm" />
						</Dialog.CloseTrigger>
					</Dialog.Content>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
};
